import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardHeader, Checkbox, Tag, Toggle } from '@opendate-app/setlist';

import { REMINDER_OFFSET_CHOICES, offsetLabel } from '../../Grants/grantReminders';

const body = { display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-lg)' };
const group = { display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-sm)' };
const tagRow = { display: 'flex', flexWrap: 'wrap', gap: 'var(--setlist-spacing-sm)' };
const heading = { margin: 0, fontWeight: 600, color: 'var(--sl-text-secondary)', fontSize: 'var(--setlist-text-sm-size)' };

const settingsFrom = (grant) => ({
  reminders_enabled: grant.reminders_enabled !== false,
  reminder_offsets: grant.reminder_offsets || [],
  reminder_team_membership_ids: grant.reminder_team_membership_ids || [],
});

const toggled = (list, value, on) => (on ? [...list.filter((item) => item !== value), value] : list.filter((item) => item !== value));

function GrantReminderSettings({ grant, owners, canManage, patchGrant }) {
  const [settings, setSettings] = useState(() => settingsFrom(grant));

  useEffect(() => {
    setSettings(settingsFrom(grant));
  }, [grant.reminders_enabled, grant.reminder_offsets, grant.reminder_team_membership_ids]);

  const save = async (field, value) => {
    setSettings((current) => ({ ...current, [field]: value }));
    const saved = await patchGrant({ [field]: value });
    if (!saved) setSettings(settingsFrom(grant));
  };

  const choicesDisabled = !canManage || !settings.reminders_enabled;
  const ownerId = grant.owner_team_membership_id;

  const toggle = (
    <Toggle
      aria-label="Send deadline reminders"
      checked={settings.reminders_enabled}
      disabled={!canManage}
      onChange={(event) => save('reminders_enabled', event.target.checked)}
    />
  );

  return (
    <Card
      header={(
        <CardHeader
          title="Deadline reminders"
          supportingText="Automatic emails before every open date."
          actions={toggle}
        />
      )}
    >
      <div style={body}>
        <div style={group}>
          <p style={heading}>Send reminders</p>
          <div style={tagRow}>
            {REMINDER_OFFSET_CHOICES.map((offset) => (
              <Tag
                key={offset}
                selectable
                selected={settings.reminder_offsets.includes(offset)}
                disabled={choicesDisabled}
                onSelectedChange={(on) => save(
                  'reminder_offsets',
                  toggled(settings.reminder_offsets, offset, on).sort((a, b) => b - a),
                )}
              >
                {offsetLabel(offset)}
              </Tag>
            ))}
          </div>
        </div>
        <div style={group}>
          <p style={heading}>Recipients</p>
          {owners.map((owner) => {
            const id = Number(owner.value);
            const isOwner = id === ownerId;
            return (
              <Checkbox
                key={id}
                label={isOwner ? `${owner.label} · Grant owner` : owner.label}
                checked={isOwner || settings.reminder_team_membership_ids.includes(id)}
                disabled={choicesDisabled || isOwner}
                onChange={(event) => save(
                  'reminder_team_membership_ids',
                  toggled(settings.reminder_team_membership_ids, id, event.target.checked),
                )}
              />
            );
          })}
        </div>
      </div>
    </Card>
  );
}

GrantReminderSettings.propTypes = {
  grant: PropTypes.shape({
    owner_team_membership_id: PropTypes.number,
    reminders_enabled: PropTypes.bool,
    reminder_offsets: PropTypes.arrayOf(PropTypes.number),
    reminder_team_membership_ids: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  owners: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
  })),
  canManage: PropTypes.bool,
  patchGrant: PropTypes.func.isRequired,
};

GrantReminderSettings.defaultProps = { owners: [], canManage: false };

export default GrantReminderSettings;
